import React from 'react';
import { AlertTriangle, FileText } from 'lucide-react';

/**
 * 증례 작성 가이드 — 템플릿 삽입 + 게시 전 확인(환자 동의 / 비식별화).
 *
 * 증례 공유는 의료법상 비밀누설 금지와 개인정보보호법 민감정보 규정의 적용을 받는다.
 * 동의·비식별 확인은 서버에서도 강제되며(CASE_CONSENT_REQUIRED), 식별정보가 남아 있으면
 * 게시 자체가 차단된다(PHI_DETECTED). 여기서는 작성 시점에 미리 알려 되돌아오는 일을 줄인다.
 */

export const CASE_TEMPLATE = `## 증례 요약
(예: 80대 여성, 의식 변화와 저혈압으로 내원)

## 병력 및 내원 경위

## 활력징후 · 검사 소견

## 경과 및 처치
(시간 순으로)

## 최종 진단

## 논의 — 배운 점
(무엇이 어려웠는지, 놓치기 쉬운 지점)

## 동료 의견을 구하고 싶은 점

---
> 본 증례는 환자(또는 법정대리인)의 동의를 얻어 개인식별정보를 제거하고 공유합니다.`;

/** 작성 중 즉시 알려주는 가벼운 검사. 최종 판정은 서버가 한다. */
export function localPhiHints(text: string): string[] {
  const hints: string[] = [];
  if (/\b\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])\s*-?\s*[1-8]\d{6}\b/.test(text)) {
    hints.push('주민등록번호로 보이는 숫자가 있습니다.');
  }
  if (/\b01[016789][-.\s]?\d{3,4}[-.\s]?\d{4}\b|\b0(?:2|3[1-3]|4[1-4]|5[1-5]|6[1-4])[-.\s]?\d{3,4}[-.\s]?\d{4}\b/.test(text)) {
    hints.push('전화번호가 있습니다.');
  }
  if (/[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/.test(text)) {
    hints.push('이메일 주소가 있습니다.');
  }
  if (/(?:환자|차트|등록|병록|입원)\s*(?:번호|No\.?|#)\s*[:：]?\s*[A-Za-z0-9-]{4,}/.test(text)) {
    hints.push('환자·차트 번호가 있습니다.');
  }
  if (/(?:환자|보호자)\s*(?:성명|이름)\s*[:：]\s*\S+/.test(text)) {
    hints.push('환자 이름이 있습니다. 연령대·성별로만 적어주세요.');
  }
  return hints;
}

interface Props {
  content: string;
  onInsertTemplate: () => void;
  consent: boolean;
  deidentified: boolean;
  onConsentChange: (v: boolean) => void;
  onDeidentifiedChange: (v: boolean) => void;
}

const CaseGuide: React.FC<Props> = ({
  content,
  onInsertTemplate,
  consent,
  deidentified,
  onConsentChange,
  onDeidentifiedChange,
}) => {
  const hints = localPhiHints(content);

  return (
    <div className="border border-gray-900 rounded-lg p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">증례 공유</p>
          <p className="text-xs text-gray-500 mt-0.5">
            동의와 비식별화를 확인해야 게시할 수 있습니다. 식별정보가 남아 있으면 서버에서 차단됩니다.
          </p>
        </div>
        <button
          type="button"
          onClick={onInsertTemplate}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors"
        >
          <FileText className="w-3.5 h-3.5" />
          템플릿 삽입
        </button>
      </div>

      {hints.length > 0 && (
        <div className="flex gap-2 border border-gray-300 rounded-md p-3">
          <AlertTriangle className="w-4 h-4 text-gray-900 shrink-0 mt-0.5" />
          <div className="text-xs text-gray-700 space-y-0.5">
            <p className="font-medium text-gray-900">게시 전에 지워주세요</p>
            {hints.map((h) => (
              <p key={h}>· {h}</p>
            ))}
          </div>
        </div>
      )}

      <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => onConsentChange(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <strong className="text-gray-900">환자 또는 법정대리인의 증례 공유 동의를 확보했습니다.</strong>
          <br />
          동의서는 소속 기관 절차에 따라 보관하세요. Pulse는 동의서 사본을 수집하지 않습니다.
        </span>
      </label>

      <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          checked={deidentified}
          onChange={(e) => onDeidentifiedChange(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <strong className="text-gray-900">이름·연락처·주민번호·차트번호·정확한 날짜 등 식별정보를 제거했습니다.</strong>
          <br />
          이미지를 첨부한다면 DICOM 메타데이터, 얼굴, 화면 속 이름·날짜까지 확인하세요.
        </span>
      </label>
    </div>
  );
};

export default CaseGuide;
